import React, { useEffect, useMemo } from 'react';
import { Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NavigationContainer } from '@react-navigation/native';
import { useCameraPermissions } from 'expo-camera';
import DualPhotoCaptureStack from './DualPhotoCaptureStack';
import { DualPhotoCaptureProvider } from './DualPhotoCaptureContext';

export interface DualPhotoCaptureFlowProps {
  onDone: (frontUri: string, backUri: string, caption?: string) => void;
  onCancel: () => void;
  onSkipWithoutPhotos?: () => void;
  onExitCamera?: () => void;
  countdownDuration?: number;
}

/**
 * Orchestrates permission → nested stack (back camera → front camera) → onDone.
 */
export default function DualPhotoCaptureFlow({
  onDone,
  onCancel,
  onSkipWithoutPhotos,
  onExitCamera,
}: DualPhotoCaptureFlowProps) {
  const [permission, requestPermission] = useCameraPermissions();
  const [caption] = React.useState('');

  const callbacks = useMemo(
    () => ({
      onDone,
      onCancel,
      onSkipWithoutPhotos,
      onExitCamera,
      caption,
    }),
    [onDone, onCancel, onSkipWithoutPhotos, onExitCamera, caption],
  );

  useEffect(() => {
    if (permission && !permission.granted) {
      requestPermission();
    }
  }, [permission, requestPermission]);

  if (!permission) {
    return (
      <SafeAreaView style={styles.permissionContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.permissionText}>Requesting camera permission...</Text>
      </SafeAreaView>
    );
  }

  if (!permission.granted) {
    return (
      <SafeAreaView style={styles.permissionContainer}>
        <Text style={styles.permissionText}>Camera permission is required to take photos.</Text>
        <TouchableOpacity style={styles.button} onPress={requestPermission}>
          <Text style={styles.buttonText}>Grant Permission</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.button, styles.cancelButton]} onPress={onCancel}>
          <Text style={styles.buttonText}>Cancel</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <DualPhotoCaptureProvider value={callbacks}>
      {/*
        Own navigation tree (Be Real modal / web has no app-level NavigationContainer).
        independent avoids clashing when this modal opens under the native app's container.
      */}
      <NavigationContainer independent>
        <DualPhotoCaptureStack />
      </NavigationContainer>
    </DualPhotoCaptureProvider>
  );
}

const styles = StyleSheet.create({
  permissionContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  permissionText: {
    color: '#fff',
    fontSize: 16,
    textAlign: 'center',
    marginVertical: 20,
  },
  button: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 25,
    marginVertical: 10,
  },
  cancelButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
  },
});
